import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import AppHeader from '../components/AppHeader';
import LoadingSpinner from '../components/LoadingSpinner';
import PhotoSlot from '../components/PhotoSlot';
import { uploadInspection, getVehicle } from '../api/vehiclesApi';
import { useToast } from '../context/ToastContext';

// mode is passed as a query param: /loads/1042/inspection?mode=pickup or ?mode=delivery
// vehicleId identifies which vehicle on this load is being inspected.
// Vehicle info (name, model, VIN) is fetched and shown READ-ONLY, since it was
// already entered by the dispatcher/admin when the vehicle was created —
// the driver only adds photos and notes here, nothing else.
const MAX_PHOTOS = 10; // was 4 — bumped to 10 per updated requirements

function VehicleInspection() {
  const { id } = useParams(); // load id
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'pickup';
  const vehicleId = searchParams.get('vehicleId');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const { register, handleSubmit } = useForm();

  const [vehicle, setVehicle] = useState(null);
  const [loadingVehicle, setLoadingVehicle] = useState(true);
  const [photos, setPhotos] = useState(Array(MAX_PHOTOS).fill(null));
  const [photoError, setPhotoError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    async function fetchVehicle() {
      try {
        const result = await getVehicle(vehicleId);
        if (result.success) {
          setVehicle(result.vehicle);
        } else {
          setSubmitError(result.message || 'Failed to load vehicle info.');
        }
      } catch (err) {
        setSubmitError('Could not connect to server.');
      } finally {
        setLoadingVehicle(false);
      }
    }

    if (vehicleId) {
      fetchVehicle();
    } else {
      setLoadingVehicle(false);
    }
  }, [vehicleId]);

  const handlePhotoChange = (index, file, previewUrl) => {
    const updated = [...photos];
    updated[index] = { file, previewUrl };
    setPhotos(updated);
    setPhotoError('');
  };

  const handlePhotoRemove = (index) => {
    const updated = [...photos];
    updated[index] = null;
    setPhotos(updated);
  };

  const onSubmit = async (data) => {
    const filledPhotos = photos.filter(Boolean);

    if (filledPhotos.length === 0) {
      setPhotoError(t('inspection.photosRequired'));
      return;
    }

    if (!vehicleId) {
      setSubmitError('Missing vehicle reference. Please go back and try again.');
      return;
    }

    setSubmitError('');
    setSubmitting(true);

    try {
      const result = await uploadInspection({
        vehicleId,
        loadId: id,
        mode,
        notes: data.notes,
        photoFiles: filledPhotos.map((p) => p.file),
      });

      if (result.success) {
        showToast('success', 'Inspection submitted successfully.');
        navigate(`/loads/${id}/vehicles/${vehicleId}`);
      } else {
        setSubmitError(result.message || 'Failed to submit inspection.');
        showToast('error', result.message || 'Failed to submit inspection.');
      }
    } catch (err) {
      setSubmitError('Could not connect to server.');
      showToast('error', t('common.networkError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingVehicle) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <AppHeader title="Loading..." showBack={true} fallbackRoute={`/loads/${id}`} />
        <LoadingSpinner fullScreen />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', paddingBottom: 100 }}>
      <AppHeader
        title={mode === 'pickup' ? t('inspection.titlePickup') : t('inspection.titleDelivery')}
        showBack={true}
        fallbackRoute={`/loads/${id}`}
      />

      <form onSubmit={handleSubmit(onSubmit)} style={{ padding: 16 }}>
        {/* Read-only vehicle info — already entered by the dispatcher, not editable here */}
        {vehicle && (
          <div style={styles.vehicleInfoCard}>
            <div style={styles.vehicleInfoTitle}>
              {vehicle.vehicle_name} {vehicle.vehicle_model}
            </div>
            <div style={styles.vehicleInfoVin}>VIN: {vehicle.vin_number}</div>
          </div>
        )}

        {/* Photo grid — up to 10 slots, 5 per row */}
        <div style={styles.sectionLabel}>
          {t('inspection.addPhoto')} ({photos.filter(Boolean).length}/{MAX_PHOTOS})
        </div>
        <div style={styles.photoGrid}>
          {photos.map((photo, index) => (
            <PhotoSlot
              key={index}
              imageUrl={photo?.previewUrl}
              onChange={(file, previewUrl) => handlePhotoChange(index, file, previewUrl)}
              onRemove={() => handlePhotoRemove(index)}
            />
          ))}
        </div>
        {photoError && <p style={styles.errorText}>{photoError}</p>}

        {/* Notes */}
        <label style={styles.label}>{t('inspection.notes')}</label>
        <textarea
          {...register('notes')}
          placeholder={t('inspection.notesPlaceholder')}
          style={{ ...styles.input, height: 90, resize: 'none' }}
        />

        {submitError && <p style={styles.errorText}>{submitError}</p>}

        <div style={styles.submitBar}>
          <button type="submit" disabled={submitting} style={styles.submitButton}>
            {submitting ? '...' : t('inspection.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}

const styles = {
  vehicleInfoCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 14,
    marginBottom: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  vehicleInfoTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 4,
  },
  vehicleInfoVin: {
    fontSize: 13,
    color: '#6b7280',
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 8,
  },
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 8,
    marginBottom: 8,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginTop: 16,
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    backgroundColor: 'white',
    fontSize: 14,
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 12,
    marginTop: 4,
  },
  submitBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'white',
    padding: 16,
    boxShadow: '0 -1px 6px rgba(0,0,0,0.08)',
  },
  submitButton: {
    width: '100%',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 15,
    padding: '14px 0',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
  },
};

export default VehicleInspection;